import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const API = '';
const tok = () => localStorage.getItem('nexus_token') || localStorage.getItem('vgo_token') || '';
const hdr = () => ({ 'Content-Type':'application/json', Authorization:`Bearer ${tok()}` });

const LEVEL_CONFIG = {
  0: { label:'Basic',                color:'#94a3b8', bg:'#f1f5f9', icon:'⚪' },
  1: { label:'Profile Verified',     color:'#f59e0b', bg:'#fef3c7', icon:'🟡' },
  2: { label:'Identity Declared',    color:'#f97316', bg:'#fff7ed', icon:'🟠' },
  3: { label:'Address & Education',  color:'#10b981', bg:'#ecfdf5', icon:'🟢' },
  4: { label:'Fully Verified',       color:'#1a3cc8', bg:'#eff6ff', icon:'🔵' },
};

// Calculate GAP Score (0-100) from profile completeness
function calcGAPScore(p) {
  if(!p) return 0;
  let score = 0;
  if(p.full_name)                           score += 10; // name
  if(p.profile_photo)                       score += 10; // photo
  if(p.bio && p.bio.length > 20)            score += 10; // bio
  if(p.current_city)                        score += 5;  // location
  if(p.id_type)                             score += 15; // identity
  if(p.skills && p.skills.length >= 3)      score += 10; // skills
  if(p.languages && p.languages.length > 0) score += 5;  // languages
  if(p.address_declared)                    score += 5;  // address
  if(p.portfolio_links && p.portfolio_links.length > 0) score += 5; // portfolio
  if(p.is_available)                        score += 5;  // available
  // Bonus from verification level
  score += (p.verification_level||0) * 4;
  return Math.min(score, 100);
}

function GAPScoreRing({ score, size=80 }) {
  const r = size/2 - 6;
  const circ = 2 * Math.PI * r;
  const filled = (score/100) * circ;
  const color = score>=80?'#1a3cc8':score>=60?'#10b981':score>=40?'#f59e0b':'#ef4444';
  return (
    <div style={{position:'relative',width:size,height:size,flexShrink:0}}>
      <svg width={size} height={size} style={{transform:'rotate(-90deg)'}}>
        <circle cx={size/2} cy={size/2} r={r} fill="none"
          stroke="#e2e8f0" strokeWidth={6}/>
        <circle cx={size/2} cy={size/2} r={r} fill="none"
          stroke={color} strokeWidth={6}
          strokeDasharray={`${filled} ${circ}`}
          strokeLinecap="round"/>
      </svg>
      <div style={{position:'absolute',inset:0,display:'flex',flexDirection:'column',
        alignItems:'center',justifyContent:'center'}}>
        <div style={{fontSize:size>60?18:13,fontWeight:900,color:color,lineHeight:1}}>
          {score}
        </div>
        <div style={{fontSize:8,color:'#94a3b8',fontWeight:600}}>GAP</div>
      </div>
    </div>
  );
}

function VerifiedBadge({ level, size='sm' }) {
  const cfg = LEVEL_CONFIG[level] || LEVEL_CONFIG[0];
  return (
    <span style={{ display:'inline-flex', alignItems:'center', gap:4,
      background:cfg.bg, color:cfg.color, borderRadius:20,
      padding: size==='lg' ? '4px 12px' : '2px 8px',
      fontSize: size==='lg' ? 12 : 10, fontWeight:700 }}>
      {cfg.icon} {cfg.label}
    </span>
  );
}

export default function GAPScreen() {
  const nav = useNavigate();
  const [myProfile, setMyProfile] = useState(null);
  const [profiles, setProfiles]   = useState([]);
  const [loading, setLoading]     = useState(true);
  const [search, setSearch]       = useState({ q:'', city:'', available:false });
  const isLoggedIn = !!tok();

  useEffect(() => {
    if (isLoggedIn) {
      fetch(`${API}/api/backend/v1/gap/profile/me`, { headers: hdr() })
        .then(r=>r.json()).then(d=>setMyProfile(d.user_id?d:null)).catch(()=>{});
    }
    loadBrowse();
  }, []);

  const loadBrowse = async (params={}) => {
    setLoading(true);
    try {
      const qs = new URLSearchParams({ limit:'24', ...params }).toString();
      const r = await fetch(`${API}/api/backend/v1/gap/browse?${qs}`, { headers: hdr() });
      const d = await r.json();
      setProfiles(d.profiles || []);
    } catch(e) {} finally { setLoading(false); }
  };

  const handleSearch = () => loadBrowse({ q:search.q, city:search.city, available:search.available?'true':'' });

  return (
    <div style={{ maxWidth:430, margin:'0 auto', background:'#f8fafc', minHeight:'100vh', paddingBottom:80 }}>

      {/* Header */}
      <div style={{ background:'linear-gradient(135deg,#1a3cc8,#2d4fd6)', padding:'52px 16px 16px', position:'sticky', top:0, zIndex:100 }}>
        <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:14 }}>
          <button onClick={()=>nav(-1)} style={{ background:'rgba(255,255,255,0.15)', border:'none', borderRadius:10, width:36, height:36, color:'#fff', fontSize:18, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center' }}>‹</button>
          <div style={{ flex:1 }}>
            <div style={{ fontSize:20, fontWeight:900, color:'#fff', letterSpacing:-0.5 }}>GAP</div>
            <div style={{ fontSize:11, color:'rgba(255,255,255,0.7)' }}>Genuine Applicant Profiles — Global</div>
          </div>
          {isLoggedIn && (
            <button onClick={()=>nav('/gap/edit')} style={{ background:'rgba(255,255,255,0.2)', border:'1px solid rgba(255,255,255,0.3)', borderRadius:20, padding:'6px 14px', color:'#fff', fontSize:11, fontWeight:700, cursor:'pointer' }}>
              {myProfile ? 'My Profile' : '+ Create'}
            </button>
          )}
        </div>

        {/* Search bar */}
        <div style={{ display:'flex', gap:6 }}>
          <input value={search.q} onChange={e=>setSearch(s=>({...s,q:e.target.value}))}
            onKeyDown={e=>e.key==='Enter'&&handleSearch()}
            placeholder="Search name, skills, bio…"
            style={{ flex:1, padding:'8px 12px', borderRadius:10, border:'none', fontSize:12, outline:'none', background:'rgba(255,255,255,0.15)', color:'#fff', '::placeholder':{color:'rgba(255,255,255,0.5)'} }} />
          <button onClick={handleSearch} style={{ background:'#fff', border:'none', borderRadius:10, padding:'8px 14px', color:'#1a3cc8', fontSize:11, fontWeight:700, cursor:'pointer' }}>Search</button>
        </div>
      </div>

      <div style={{ padding:'12px 16px' }}>

        {/* Filter row */}
        <div style={{ display:'flex', gap:8, marginBottom:14, alignItems:'center' }}>
          <input value={search.city} onChange={e=>setSearch(s=>({...s,city:e.target.value}))}
            placeholder="City" style={{ flex:1, padding:'7px 10px', borderRadius:10, border:'1px solid #e2e8f0', fontSize:12, outline:'none', background:'#fff' }} />
          <label style={{ display:'flex', alignItems:'center', gap:5, fontSize:11, fontWeight:600, color:'#64748b', cursor:'pointer', whiteSpace:'nowrap' }}>
            <input type="checkbox" checked={search.available} onChange={e=>setSearch(s=>({...s,available:e.target.checked}))} />
            Available
          </label>
          <button onClick={handleSearch} style={{ background:'#1a3cc8', border:'none', borderRadius:10, padding:'7px 14px', color:'#fff', fontSize:11, fontWeight:700, cursor:'pointer' }}>Go</button>
        </div>

        {/* My profile quick card */}
        {myProfile && (
          <div style={{background:'#fff',border:'2px solid #1a3cc8',borderRadius:16,
            padding:'14px',marginBottom:14,display:'flex',alignItems:'center',gap:12}}>
            <GAPScoreRing score={calcGAPScore(myProfile)} size={72}/>
            <div style={{flex:1,minWidth:0}}>
              <div style={{fontSize:14,fontWeight:800,color:'#1e293b'}}>
                {myProfile.display_name||myProfile.full_name}
              </div>
              <VerifiedBadge level={myProfile.verification_level||0} size='sm'/>
              <div style={{fontSize:11,color:'#64748b',marginTop:4}}>
                {myProfile.current_city&&`📍 ${myProfile.current_city} · `}
                {myProfile.skills?.length>0&&`💡 ${myProfile.skills.length} skills`}
              </div>
            </div>
            <div style={{display:'flex',flexDirection:'column',gap:6}}>
              <button onClick={e=>{e.stopPropagation();nav('/gap/edit')}}
                style={{background:'#1a3cc8',border:'none',borderRadius:10,
                  padding:'7px 12px',color:'#fff',fontSize:11,fontWeight:700,cursor:'pointer'}}>
                ✏️ Edit
              </button>
              <button onClick={e=>{e.stopPropagation();nav('/gap/access')}}
                style={{background:'#f1f5f9',border:'none',borderRadius:10,
                  padding:'7px 12px',color:'#334155',fontSize:11,fontWeight:700,cursor:'pointer'}}>
                🔒 Access
              </button>
            </div>
          </div>
        )}

        {/* Stats row */}
        <div style={{ display:'flex', gap:8, marginBottom:14 }}>
          {[{icon:'🔵',label:'Fully Verified'},{icon:'🟢',label:'High Trust'},{icon:'🌍',label:'Global'}].map((s,i)=>(
            <div key={i} style={{ flex:1, background:'#fff', borderRadius:10, padding:'8px', textAlign:'center', border:'1px solid #e2e8f0' }}>
              <div style={{ fontSize:16 }}>{s.icon}</div>
              <div style={{ fontSize:9, color:'#64748b', fontWeight:600, marginTop:2 }}>{s.label}</div>
            </div>
          ))}
        </div>

        {/* Results */}
        {loading ? (
          <div style={{ textAlign:'center', padding:60, color:'#94a3b8' }}>
            <div style={{ fontSize:32, marginBottom:10 }}>🔍</div>
            <div style={{ fontSize:13 }}>Loading profiles…</div>
          </div>
        ) : profiles.length === 0 ? (
          <div style={{ textAlign:'center', padding:'50px 20px' }}>
            <div style={{ fontSize:48, marginBottom:12 }}>📋</div>
            <div style={{ fontSize:16, fontWeight:700, color:'#1e293b', marginBottom:6 }}>No profiles found</div>
            <div style={{ fontSize:12, color:'#94a3b8', marginBottom:20 }}>Be the first to create a GAP profile!</div>
            {isLoggedIn && <button onClick={()=>nav('/gap/edit')} style={{ background:'#1a3cc8', border:'none', borderRadius:10, padding:'10px 24px', color:'#fff', fontSize:13, fontWeight:700, cursor:'pointer' }}>Create My Profile</button>}
          </div>
        ) : (
          profiles.map(p => (
            <div key={p.user_id} onClick={()=>nav('/gap/profile/'+p.user_id)}
              style={{ background:'#fff', borderRadius:14, marginBottom:10, overflow:'hidden', boxShadow:'0 1px 6px rgba(0,0,0,0.06)', border:'1px solid #f1f5f9', cursor:'pointer' }}>
              <div style={{ padding:'14px 14px 12px' }}>
                <div style={{ display:'flex', gap:10, alignItems:'flex-start' }}>
                  <div style={{ width:48, height:48, borderRadius:24, background: p.profile_photo?`url(${p.profile_photo}) center/cover`:'linear-gradient(135deg,#1a3cc8,#7c3aed)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:20, fontWeight:800, color:'#fff', flexShrink:0 }}>
                    {!p.profile_photo && p.display_name[0]}
                  </div>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ display:'flex', alignItems:'center', gap:6, marginBottom:3, flexWrap:'wrap' }}>
                      <div style={{ fontSize:15, fontWeight:800, color:'#0f172a' }}>{p.display_name}</div>
                      {p.is_available && <span style={{ fontSize:9, background:'#ecfdf5', color:'#065f46', borderRadius:6, padding:'2px 6px', fontWeight:700 }}>✓ Available</span>}
                    </div>
                    <div style={{ fontSize:11, color:'#64748b', marginBottom:4 }}>
                      {[p.current_city, p.current_country].filter(Boolean).join(', ')}
                    </div>
                    <VerifiedBadge level={p.verification_level} />
                  </div>
                </div>

                {p.bio && <div style={{ fontSize:12, color:'#475569', marginTop:8, lineHeight:1.5, maxHeight:32, overflow:'hidden' }}>{p.bio}</div>}

                {p.skills?.length > 0 && (
                  <div style={{ display:'flex', gap:5, flexWrap:'wrap', marginTop:8 }}>
                    {p.skills.slice(0,4).map((sk,i)=>(
                      <span key={i} style={{ background:'#eff6ff', color:'#1a3cc8', padding:'3px 8px', borderRadius:8, fontSize:10, fontWeight:600 }}>{sk}</span>
                    ))}
                    {p.skills.length > 4 && <span style={{ fontSize:10, color:'#94a3b8' }}>+{p.skills.length-4}</span>}
                  </div>
                )}

                <div style={{ display:'flex', gap:12, marginTop:8 }}>
                  {p.exp_count > 0 && <span style={{ fontSize:10, color:'#64748b' }}>💼 {p.exp_count} experience</span>}
                  {p.edu_count > 0 && <span style={{ fontSize:10, color:'#64748b' }}>🎓 {p.edu_count} education</span>}
                  {p.profile_views > 0 && <span style={{ fontSize:10, color:'#94a3b8' }}>👁 {p.profile_views}</span>}
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
